export interface UserMessage {
  id: string
  user_id: string
  title: string
  content: string
  message_type: 'notification' | 'announcement' | 'alert' | 'update' | 'welcome'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  is_read: boolean
  is_archived: boolean
  action_url?: string
  action_label?: string
  metadata: Record<string, any>
  expires_at?: string
  created_at: string
  updated_at: string
  read_at?: string
  archived_at?: string
}

interface MessageState {
  messages: UserMessage[]
  unreadCount: number
  isLoading: boolean
  activeTab: 'inbox' | 'archived'
}

export const useMessageStore = defineStore('messageStore', {
  state: (): MessageState => ({
    messages: [],
    unreadCount: 0,
    isLoading: false,
    activeTab: 'inbox'
  }),

  getters: {
    inboxMessages: (state) => 
      state.messages.filter(m => !m.is_archived && (!m.expires_at || new Date(m.expires_at) > new Date())),
    
    archivedMessages: (state) => 
      state.messages.filter(m => m.is_archived),
    
    unreadMessages: (state) => 
      state.messages.filter(m => !m.is_read && !m.is_archived),
    
    priorityMessages: (state) => 
      state.messages.filter(m => m.priority === 'urgent' || m.priority === 'high').slice(0, 3),

    messagesByType: (state) => (type: UserMessage['message_type']) =>
      state.messages.filter(m => m.message_type === type)
  },

  actions: {
    async fetchMessages() {
      this.isLoading = true
      try {
        const supabase = useSupabaseClient()
        const user = useSupabaseUser()
        
        if (!user.value) {
          throw new Error('No authenticated user')
        }

        const { data: messages, error } = await supabase
          .from('user_messages')
          .select('*')
          .eq('user_id', user.value.id)
          .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
          .order('created_at', { ascending: false })
        
        if (error) throw error
        
        this.messages = messages || []
        this.updateUnreadCount()
      } catch (error) {
        console.error('Error fetching messages:', error)
      } finally {
        this.isLoading = false
      }
    },

    async markAsRead(messageId: string) {
      try {
        const supabase = useSupabaseClient()
        
        const { error } = await supabase.rpc('mark_message_as_read', {
          message_id: messageId
        })
        
        if (error) throw error
        
        const message = this.messages.find(m => m.id === messageId)
        if (message) {
          message.is_read = true
          message.read_at = new Date().toISOString()
          this.updateUnreadCount()
        }
      } catch (error) {
        console.error('Error marking message as read:', error)
      }
    },

    async archiveMessage(messageId: string) {
      try {
        const supabase = useSupabaseClient()
        
        const { error } = await supabase.rpc('archive_message', {
          message_id: messageId
        })
        
        if (error) throw error
        
        const message = this.messages.find(m => m.id === messageId)
        if (message) {
          message.is_archived = true
          message.archived_at = new Date().toISOString()
          this.updateUnreadCount()
        }
      } catch (error) {
        console.error('Error archiving message:', error)
      }
    },

    async unarchiveMessage(messageId: string) {
      try {
        const supabase = useSupabaseClient()
        
        const { error } = await supabase.rpc('unarchive_message', {
          message_id: messageId
        })
        
        if (error) throw error
        
        const message = this.messages.find(m => m.id === messageId)
        if (message) {
          message.is_archived = false
          message.archived_at = undefined
        }
      } catch (error) {
        console.error('Error unarchiving message:', error)
      }
    },

    // Real-time handlers
    handleNewMessage(message: UserMessage) {
      // Add new message and sort by created_at descending
      this.messages.unshift(message)
      this.updateUnreadCount()
    },

    handleMessageUpdate(updatedMessage: UserMessage) {
      const index = this.messages.findIndex(m => m.id === updatedMessage.id)
      if (index !== -1) {
        this.messages[index] = updatedMessage
        this.updateUnreadCount()
      }
    },

    handleMessageDelete(deletedMessage: UserMessage) {
      const index = this.messages.findIndex(m => m.id === deletedMessage.id)
      if (index !== -1) {
        this.messages.splice(index, 1)
        this.updateUnreadCount()
      }
    },

    updateUnreadCount() {
      this.unreadCount = this.unreadMessages.length
    },



    setActiveTab(tab: 'inbox' | 'archived') {
      this.activeTab = tab
    },

    // Utility methods
    getMessageIcon(type: UserMessage['message_type']) {
      const icons = {
        notification: 'solar:bell-linear',
        announcement: 'fluent:megaphone-16-regular',
        alert: 'akar-icons:triangle-alert-fill',
        update: 'material-symbols:bigtop-updates-rounded',
        welcome: 'solar:hand-shake-line-duotone'
      }
      return icons[type] || 'lucide:message-circle'
    },

    getPriorityColor(priority: UserMessage['priority']) {
      const colors = {
        low: 'text-slate-500',
        medium: 'text-blue-600',
        high: 'text-orange-600',
        urgent: 'text-red-600'
      }
      return colors[priority] || 'text-slate-500'
    },

    formatTimeAgo(dateString: string) {
      const date = new Date(dateString)
      const now = new Date()
      const diffInMs = now.getTime() - date.getTime()
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
      const diffInHours = Math.floor(diffInMinutes / 60)
      const diffInDays = Math.floor(diffInHours / 24)

      if (diffInMinutes < 1) return 'Just now'
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`
      if (diffInHours < 24) return `${diffInHours}h ago`
      if (diffInDays < 7) return `${diffInDays}d ago`
      
      return date.toLocaleDateString()
    }
  }
}) 